import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { StarIcon, TrashIcon, CheckIcon } from '@heroicons/react/24/solid';
import { useBurstDetails, usePhotoActions } from '../hooks/useAPI';

interface BurstViewerProps {
  burstId: string;
  onClose: () => void;
}

interface Photo {
  photo_id: string;
  filename: string;
  thumbnail_url?: string;
  capture_date: string;
  quality_score?: number;
  storage_tier?: 'INSTANT' | 'STANDARD' | 'ARCHIVE';
  status: string;
  exif?: {
    camera_model?: string;
    lens_model?: string;
    focal_length?: number;
    aperture?: number;
    shutter_speed?: string;
    iso?: number;
  };
}

export const BurstViewer: React.FC<BurstViewerProps> = ({ burstId, onClose }) => {
  const { burst, recommendations, loading, error } = useBurstDetails(burstId);
  const { batchUpdatePhotoStatus, updating } = usePhotoActions();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<string>>(new Set());

  const thumbnailStripRef = useRef<HTMLDivElement>(null);

  // Load photos from burst data
  useEffect(() => {
    if (burst && recommendations) {
      // Combine photos from all recommendation categories
      const allPhotos = [
        ...(recommendations.recommendations?.keep || []),
        ...(recommendations.recommendations?.review || []),
        ...(recommendations.recommendations?.delete || []),
      ] as Photo[];

      // Remove duplicates and sort by capture date
      const uniquePhotos = Array.from(
        new Map(allPhotos.map(p => [p.photo_id, p])).values()
      ).sort((a, b) => new Date(a.capture_date).getTime() - new Date(b.capture_date).getTime());

      setPhotos(uniquePhotos);
    }
  }, [burst, recommendations]);

  // Toggle photo selection
  const togglePhotoSelection = (photoId: string) => {
    const newSelection = new Set(selectedPhotoIds);
    if (newSelection.has(photoId)) {
      newSelection.delete(photoId);
    } else {
      newSelection.add(photoId);
    }
    setSelectedPhotoIds(newSelection);
  };

  // Scroll thumbnail strip to keep current photo visible
  useEffect(() => {
    if (thumbnailStripRef.current && photos.length > 0) {
      const thumbnailElements = thumbnailStripRef.current.children;
      const currentThumbnail = thumbnailElements[currentIndex] as HTMLElement;

      if (currentThumbnail) {
        currentThumbnail.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }, [currentIndex, photos.length]);

  // Keyboard navigation and shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Don't interfere with input fields
      if ((e.target as HTMLElement).tagName === 'INPUT') return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          setCurrentIndex(prev => Math.max(0, prev - 1));
          break;
        case 'ArrowRight':
          e.preventDefault();
          setCurrentIndex(prev => Math.min(photos.length - 1, prev + 1));
          break;
        case 'k':
        case 'K':
          e.preventDefault();
          // Keep current photo
          if (photos[currentIndex]) {
            batchUpdatePhotoStatus([photos[currentIndex].photo_id], 'flagged_for_edit');
          }
          break;
        case 'd':
        case 'D':
          e.preventDefault();
          // Cull current photo
          if (photos[currentIndex]) {
            batchUpdatePhotoStatus([photos[currentIndex].photo_id], 'culled');
          }
          break;
        case ' ':
          e.preventDefault();
          // Toggle selection of current photo
          if (photos[currentIndex]) {
            togglePhotoSelection(photos[currentIndex].photo_id);
          }
          break;
        case 'a':
        case 'A':
          if (e.metaKey || e.ctrlKey) {
            e.preventDefault();
            // Select all photos
            setSelectedPhotoIds(new Set(photos.map(p => p.photo_id)));
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [photos, currentIndex, onClose, batchUpdatePhotoStatus, togglePhotoSelection]);

  // Get quality tier for a photo (based on quality score, not storage tier)
  const getQualityTier = (photo: Photo): 'hot' | 'warm' | 'cold' => {
    // Determine tier from quality score
    if (photo.quality_score !== undefined) {
      if (photo.quality_score >= 0.7) return 'hot';
      if (photo.quality_score >= 0.4) return 'warm';
      return 'cold';
    }

    // Check which recommendation category it's in
    if (recommendations?.recommendations?.keep?.some((p: Photo) => p.photo_id === photo.photo_id)) {
      return 'hot';
    } else if (recommendations?.recommendations?.review?.some((p: Photo) => p.photo_id === photo.photo_id)) {
      return 'warm';
    }
    return 'cold';
  };

  // Get quality color
  const getQualityColor = (tier: 'hot' | 'warm' | 'cold') => {
    switch (tier) {
      case 'hot': return 'bg-green-500';
      case 'warm': return 'bg-yellow-500';
      case 'cold': return 'bg-red-500';
    }
  };

  // Quick actions
  const handleKeepTop = async (count: number = 5) => {
    const topPhotos = photos
      .sort((a, b) => (b.quality_score || 0) - (a.quality_score || 0))
      .slice(0, count)
      .map(p => p.photo_id);

    await batchUpdatePhotoStatus(topPhotos, 'flagged_for_edit');
    onClose();
  };

  const handleCullCold = async () => {
    const coldPhotos = photos
      .filter(p => getQualityTier(p) === 'cold')
      .map(p => p.photo_id);

    await batchUpdatePhotoStatus(coldPhotos, 'culled');
    onClose();
  };

  const handleCullAll = async () => {
    await batchUpdatePhotoStatus(Array.from(selectedPhotoIds), 'culled');
    setSelectedPhotoIds(new Set());
  };

  const handleFlagAll = async () => {
    await batchUpdatePhotoStatus(Array.from(selectedPhotoIds), 'flagged_for_edit');
    setSelectedPhotoIds(new Set());
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mb-4" />
          <p className="text-white text-lg">Loading burst...</p>
        </div>
      </div>
    );
  }

  if (error || !burst || photos.length === 0) {
    return (
      <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center">
        <div className="bg-white dark:bg-neutral-800 rounded-lg p-8 max-w-md text-center">
          <p className="text-neutral-900 dark:text-neutral-100 text-lg mb-4">
            {error || 'No photos found in this burst'}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const currentPhoto = photos[currentIndex];
  const currentTier = getQualityTier(currentPhoto);
  const burstDuration = burst.end_time && burst.start_time
    ? (new Date(burst.end_time).getTime() - new Date(burst.start_time).getTime()) / 1000
    : 0;

  // Calculate tier counts
  const tierCounts = {
    hot: photos.filter(p => getQualityTier(p) === 'hot').length,
    warm: photos.filter(p => getQualityTier(p) === 'warm').length,
    cold: photos.filter(p => getQualityTier(p) === 'cold').length,
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex flex-col"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-neutral-900/50 border-b border-neutral-700">
        <div className="flex items-center gap-4">
          <h2 className="text-white text-xl font-semibold">
            Burst: {photos.length} photos • {burstDuration.toFixed(2)}s
          </h2>
          <div className="flex items-center gap-2 text-sm">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-white/80">HOT ({tierCounts.hot})</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <span className="text-white/80">WARM ({tierCounts.warm})</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-white/80">COLD ({tierCounts.cold})</span>
            </div>
          </div>
        </div>

        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
        >
          <XMarkIcon className="w-6 h-6 text-white" />
        </button>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Current Photo View */}
        <div className="flex-1 flex items-center justify-center relative bg-black">
          {/* Navigation Buttons */}
          <button
            onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
            disabled={currentIndex === 0}
            className="absolute left-4 z-10 p-3 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeftIcon className="w-8 h-8 text-white" />
          </button>

          <button
            onClick={() => setCurrentIndex(prev => Math.min(photos.length - 1, prev + 1))}
            disabled={currentIndex === photos.length - 1}
            className="absolute right-4 z-10 p-3 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRightIcon className="w-8 h-8 text-white" />
          </button>

          {/* Photo */}
          <motion.img
            key={currentPhoto.photo_id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            src={currentPhoto.thumbnail_url || `https://via.placeholder.com/800x600?text=${currentPhoto.filename}`}
            alt={currentPhoto.filename}
            className="max-h-full max-w-full object-contain"
          />

          {/* Quality Tier Badge */}
          <div className="absolute top-4 right-4">
            <div className={`px-4 py-2 rounded-lg ${getQualityColor(currentTier)} text-white font-semibold shadow-lg`}>
              {currentTier.toUpperCase()}
              {currentPhoto.quality_score !== undefined && (
                <span className="ml-2 opacity-80">
                  {(currentPhoto.quality_score * 100).toFixed(0)}%
                </span>
              )}
            </div>
          </div>

          {/* Photo Info Overlay */}
          <div className="absolute bottom-4 left-4 right-4 bg-black/50 backdrop-blur-sm rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-white font-medium">{currentPhoto.filename}</p>
              <p className="text-white/60 text-sm">
                {currentIndex + 1} / {photos.length}
              </p>
            </div>
            {currentPhoto.exif && (
              <p className="text-white/80 text-sm font-mono">
                {currentPhoto.exif.camera_model} • {currentPhoto.exif.lens_model}
                {' • '}
                {currentPhoto.exif.shutter_speed} • f/{currentPhoto.exif.aperture} • ISO {currentPhoto.exif.iso} • {currentPhoto.exif.focal_length}mm
              </p>
            )}
          </div>
        </div>

        {/* Timeline Scrubber */}
        <div className="bg-neutral-900 px-6 py-4 border-t border-neutral-700">
          <div className="mb-4">
            <div className="relative h-2 bg-neutral-700 rounded-full overflow-hidden cursor-pointer"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const percentage = x / rect.width;
                const newIndex = Math.floor(percentage * photos.length);
                setCurrentIndex(Math.max(0, Math.min(photos.length - 1, newIndex)));
              }}
            >
              {/* Quality gradient */}
              <div className="absolute inset-0 flex">
                {photos.map((photo, index) => {
                  const tier = getQualityTier(photo);
                  const width = `${100 / photos.length}%`;
                  return (
                    <div
                      key={photo.photo_id}
                      className={`${getQualityColor(tier)} transition-opacity ${
                        index === currentIndex ? 'opacity-100' : 'opacity-50'
                      }`}
                      style={{ width }}
                    />
                  );
                })}
              </div>

              {/* Current position indicator */}
              <div
                className="absolute top-0 bottom-0 w-1 bg-white shadow-lg"
                style={{ left: `${(currentIndex / (photos.length - 1 || 1)) * 100}%` }}
              />
            </div>
          </div>

          {/* Thumbnail Strip */}
          <div
            ref={thumbnailStripRef}
            className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-neutral-600 scrollbar-track-neutral-800"
          >
            {photos.map((photo, index) => {
              const tier = getQualityTier(photo);
              const isSelected = selectedPhotoIds.has(photo.photo_id);

              return (
                <motion.button
                  key={photo.photo_id}
                  onClick={() => setCurrentIndex(index)}
                  onDoubleClick={() => togglePhotoSelection(photo.photo_id)}
                  className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden transition-all ${
                    index === currentIndex
                      ? 'ring-4 ring-white scale-110'
                      : 'hover:scale-105 opacity-70 hover:opacity-100'
                  }`}
                  whileHover={{ y: -4 }}
                >
                  <img
                    src={photo.thumbnail_url || `https://via.placeholder.com/80?text=${index + 1}`}
                    alt={`Frame ${index + 1}`}
                    className="w-full h-full object-cover"
                  />

                  {/* Quality indicator */}
                  <div className={`absolute top-0 left-0 right-0 h-1 ${getQualityColor(tier)}`} />

                  {/* Selection indicator */}
                  {isSelected && (
                    <div className="absolute inset-0 bg-primary-400/30 border-2 border-primary-400 flex items-center justify-center">
                      <CheckIcon className="w-6 h-6 text-white drop-shadow-lg" />
                    </div>
                  )}

                  {/* Frame number */}
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs text-center py-0.5">
                    {index + 1}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer with Actions */}
      <div className="flex items-center justify-between px-6 py-4 bg-neutral-900/50 border-t border-neutral-700">
        <div className="flex items-center gap-3">
          {selectedPhotoIds.size > 0 ? (
            <>
              <span className="text-white font-medium">{selectedPhotoIds.size} selected</span>
              <button
                onClick={handleFlagAll}
                disabled={updating}
                className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition-colors"
              >
                <StarIcon className="w-4 h-4" />
                Flag Selected
              </button>
              <button
                onClick={handleCullAll}
                disabled={updating}
                className="flex items-center gap-2 px-4 py-2 bg-neutral-600 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors"
              >
                <TrashIcon className="w-4 h-4" />
                Cull Selected
              </button>
              <button
                onClick={() => setSelectedPhotoIds(new Set())}
                className="px-4 py-2 text-white/70 hover:text-white transition-colors"
              >
                Clear Selection
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => handleKeepTop(5)}
                disabled={updating}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors font-medium"
              >
                Keep Top 5
              </button>
              <button
                onClick={handleCullCold}
                disabled={updating}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors font-medium"
              >
                Cull All Cold ({tierCounts.cold})
              </button>
            </>
          )}
        </div>

        <div className="text-white/60 text-sm">
          Use ← → to navigate • Double-click to select • ESC to close
        </div>
      </div>
    </motion.div>
  );
};
